/**
 * Minimap - Small overview of the world with navigation
 */

import { useRef, useEffect, useCallback } from 'react';
import { useSimulationStore, selectMyCreature } from '../stores/simulationStore';
import { FoodType } from '../types/simulation';

interface MinimapProps {
  worldWidth: number;
  worldHeight: number;
  onNavigate: (x: number, y: number) => void;
}

const MINIMAP_SIZE = 150;
const THROTTLE_MS = 100; // Limit redraws to ~10fps

export function Minimap({ worldWidth, worldHeight, onNavigate }: MinimapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastDrawRef = useRef<number>(0);
  const rafIdRef = useRef<number | null>(null);
  const creatures = useSimulationStore((state) => state.creatures);
  const food = useSimulationStore((state) => state.food);
  const myCreature = useSimulationStore(selectMyCreature);
  const cameraX = useSimulationStore((state) => state.cameraX);
  const cameraY = useSimulationStore((state) => state.cameraY);
  const cameraZoom = useSimulationStore((state) => state.cameraZoom);

  const scaleX = MINIMAP_SIZE / worldWidth;
  const scaleY = MINIMAP_SIZE / worldHeight;

  // Draw minimap with throttling
  useEffect(() => {
    // Cancel any pending animation frame
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Throttle redraws
    const now = performance.now();
    const timeSinceLastDraw = now - lastDrawRef.current;

    if (timeSinceLastDraw < THROTTLE_MS) {
      // Schedule redraw after throttle period
      rafIdRef.current = requestAnimationFrame(() => {
        lastDrawRef.current = performance.now();
        drawMinimap();
      });
      return;
    }

    lastDrawRef.current = now;
    drawMinimap();

    function drawMinimap() {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear
      ctx.fillStyle = '#0a0a0f';
      ctx.fillRect(0, 0, MINIMAP_SIZE, MINIMAP_SIZE);

      // Draw border
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, MINIMAP_SIZE, MINIMAP_SIZE);

      // Draw food
      for (const f of food) {
        const x = f.x * scaleX;
        const y = f.y * scaleY;

        // Color based on food type
        switch (f.type) {
          case FoodType.PLANT:
            ctx.fillStyle = '#00ff88';
            break;
          case FoodType.MEAT:
            ctx.fillStyle = '#ff4444';
            break;
          case FoodType.MINERAL:
            ctx.fillStyle = '#4488ff';
            break;
          default:
            ctx.fillStyle = '#00ff88';
        }

        ctx.beginPath();
        ctx.arc(x, y, 1, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw creatures
      for (const c of creatures) {
        const x = c.x * scaleX;
        const y = c.y * scaleY;

        // Check if this is my creature
        const isMyCreature = myCreature && c.id === myCreature.id;

        if (isMyCreature) {
          // Draw golden ring for my creature
          ctx.strokeStyle = '#ffd700';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(x, y, 5, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Color based on owner or creature color
        if (c.isPlayerControlled) {
          // Player-controlled creatures are brighter
          ctx.fillStyle = `hsl(${c.color.h}, 80%, 60%)`;
        } else if (c.ownerId) {
          // AI-controlled (disconnected player)
          ctx.fillStyle = `hsl(${c.color.h}, 50%, 40%)`;
        } else {
          // Wild AI creatures
          ctx.fillStyle = `hsl(${c.color.h}, 60%, 50%)`;
        }

        ctx.beginPath();
        ctx.arc(x, y, isMyCreature ? 4 : 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw viewport rectangle
      // Estimate visible area based on camera
      const viewWidth = (window.innerWidth / cameraZoom) * scaleX;
      const viewHeight = (window.innerHeight / cameraZoom) * scaleY;
      const viewX = (cameraX - window.innerWidth / (2 * cameraZoom)) * scaleX;
      const viewY = (cameraY - window.innerHeight / (2 * cameraZoom)) * scaleY;

      ctx.strokeStyle = '#ffffff44';
      ctx.lineWidth = 1;
      ctx.strokeRect(viewX, viewY, viewWidth, viewHeight);
    }

    // Cleanup: cancel pending animation frame
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [creatures, food, myCreature, cameraX, cameraY, cameraZoom, scaleX, scaleY]);

  // Handle click to navigate
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Convert to world coordinates
      const worldX = clickX / scaleX;
      const worldY = clickY / scaleY;

      onNavigate(worldX, worldY);
    },
    [scaleX, scaleY, onNavigate]
  );

  return (
    <div className="minimap-container">
      <canvas
        ref={canvasRef}
        width={MINIMAP_SIZE}
        height={MINIMAP_SIZE}
        className="minimap-canvas"
        onClick={handleClick}
        title="Click para navegar"
      />
      <div className="minimap-legend">
        <span className="legend-item plant">Plantas</span>
        <span className="legend-item meat">Carne</span>
        <span className="legend-item creature">Criaturas</span>
      </div>
    </div>
  );
}
